import { z } from '@hono/zod-openapi'

export const FileMetadataSchema = z.object({
  id: z.string().uuid().openapi({ example: 'file-a1b2c3d4-e5f6' }),
  districtId: z.string().min(1).max(64),
  studentId: z.string().uuid().nullable(),
  name: z.string().min(1).max(255).openapi({ example: 'iep-jane-doe-2026.pdf' }),
  mimeType: z.string().openapi({ example: 'application/pdf' }),
  sizeBytes: z.number().int().min(0).openapi({ example: 1048576 }),
  parentId: z.string().uuid().nullable().openapi({ description: 'Parent folder ID' }),
  folder: z.boolean().default(false),
  uploadedBy: z.string().uuid().openapi({ description: 'User who uploaded' }),
  encryptedAt: z.string().datetime().nullable().openapi({ description: 'When client-side encryption was applied' }),
  encryptionAlgorithm: z.string().nullable().openapi({ example: 'AES-256-GCM' }),
  checksumSha256: z.string().length(64).nullable().openapi({ description: 'SHA-256 of ciphertext' }),
  checksumSha256Plaintext: z.string().length(64).nullable().openapi({ description: 'SHA-256 of plaintext (never stored server-side — for client verification)' }),
  signedUrl: z.string().url().nullable().openapi({ example: 'https://storage.cetused.com/files/abc123?signature=...' }),
  signedUrlExpiresAt: z.string().datetime().nullable(),
  retentionDays: z.number().int().nullable().openapi({ description: 'WORM retention period' }),
  tags: z.array(z.string()).nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
}).openapi('FileMetadata', { description: 'File or folder metadata record' })

export const FolderSchema = FileMetadataSchema.extend({
  folder: z.literal(true),
  childCount: z.number().int().nullable(),
}).openapi('Folder', { description: 'A folder (directory) in the file store' })

export const UploadRequestSchema = z.object({
  filename: z.string().min(1).max(255),
  mimeType: z.string(),
  sizeBytes: z.number().int().min(1).max(524288000).openapi({ description: 'Max 500 MB' }),
  parentId: z.string().uuid().nullable(),
  studentId: z.string().uuid().nullable(),
  checksumSha256: z.string().length(64).openapi({ description: 'SHA-256 of plaintext (client-computed)' }),
  encryptionAlgorithm: z.string().default('AES-256-GCM'),
}).openapi('UploadRequest', { description: 'Pre-upload request — server returns a presigned URL' })

export const UploadResponseSchema = z.object({
  id: z.string().uuid(),
  uploadUrl: z.string().url().openapi({ description: 'Presigned upload URL (temporary)' }),
  uploadExpiresAt: z.string().datetime(),
  expectedChecksum: z.string().length(64),
  headers: z.record(z.string()).nullable().openapi({ description: 'Required upload headers' }),
}).openapi('UploadResponse')

export const FileAccessGrantSchema = z.object({
  id: z.string().uuid(),
  fileId: z.string().uuid(),
  granteeId: z.string().uuid().openapi({ description: 'User or role ID' }),
  grantType: z.enum(['user', 'role', 'district']),
  permission: z.enum(['read', 'write', 'delete', 'manage']),
  expiresAt: z.string().datetime().nullable(),
  grantedBy: z.string().uuid(),
  createdAt: z.string().datetime(),
}).openapi('FileAccessGrant', { description: 'Access grant for a file or folder' })

export const FileListSchema = z.object({
  data: z.array(FileMetadataSchema),
  total: z.number().int(),
  folder: FileMetadataSchema.nullable().openapi({ description: 'Current folder context' }),
  path: z.array(z.object({ id: z.string().uuid(), name: z.string() })).nullable().openapi({ description: 'Breadcrumb path' }),
}).openapi('FileList')

export type FileMetadata = z.infer<typeof FileMetadataSchema>
export type Folder = z.infer<typeof FolderSchema>
export type UploadRequest = z.infer<typeof UploadRequestSchema>
export type UploadResponse = z.infer<typeof UploadResponseSchema>
export type FileAccessGrant = z.infer<typeof FileAccessGrantSchema>
export type FileList = z.infer<typeof FileListSchema>
